// distinctValues.js — valores distintos de una columna de una capa, para el
// multiselect buscable del operador "En uno de" (AttributeFilter.vue).
//
// Un único punto de despacho por proveedor (arcgis/upload/geoserver), con un
// contrato de retorno uniforme: { supported, values, exceeded }. `supported:
// false` es la señal para que el caller caiga al input de texto libre
// existente — nunca se tira un error hacia la UI por un servicio caído o sin
// soporte, la feature simplemente no se activa para esa capa.
//
// Fase 1: arcgis (returnDistinctValues nativo) y upload (dataset completo ya
// en memoria vía importedLayerRegistry). Fase 2: geoserver (WPS
// gs:PagedUnique) — verificado en vivo contra GeoServer 2.23.6 (2026-07-31):
// DescribeProcess confirma inputs features/fieldName/startIndex/maxFeatures
// y salida application/json {featureTypeName, fieldName, size, values};
// probado con éxito contra una capa GeoServer común (topp:states) y contra
// una Capa_Terreno real con nombre de campo con punto
// (propertiesPublic.campo_mrytlu71_2) y con búsqueda server-side vía
// ogc:PropertyIsLike — nada de esto se adivinó.
import { UrlHandler } from "@/services/utils/UrlHandler.js";
import { attributesTableService } from "./attributesTable.js";
import { importedLayerRegistry } from "./importedLayerRegistry.js";

const MAX_DISTINCT_OPTIONS = 500;

// Por (layer.id, field) — vive lo que dura la carga de la página, sin
// invalidación explícita (mismo criterio que metadataCache en
// attributesTable.js). clearDistinctValuesCache() existe solo para tests.
const cache = new Map();

const dedupeSorted = (rawValues) => {
  const set = new Set();
  rawValues.forEach((value) => {
    if (value === null || value === undefined || value === "") return;
    set.add(String(value));
  });
  return Array.from(set)
    .sort((a, b) => a.localeCompare(b, "es", { numeric: true, sensitivity: "base" }))
    .slice(0, MAX_DISTINCT_OPTIONS);
};

const emptyResult = () => ({ supported: false, values: [], exceeded: false });

// upload (archivos importados): el dataset completo ya está en memoria del
// lado del cliente (importedLayerRegistry) — nunca hay viaje a servidor, ni
// truncamiento real (solo el tope MAX_DISTINCT_OPTIONS por prolijidad de UI).
// Usa SIEMPRE entry.records, nunca entry.visibleRecords: ese último se
// reemplaza cada vez que se aplica un filtro (ver setVisibleRecords en
// importedLayerRegistry.js) — leerlo daría los valores distintos del
// subconjunto YA filtrado, justo el bug que este multiselect busca evitar.
const fetchUpload = async (layer, field) => {
  const entry = importedLayerRegistry.get(layer.id);
  if (!entry) return emptyResult();
  const values = dedupeSorted(entry.records.map((record) => record.row[field]));
  return { supported: true, values, exceeded: values.length >= MAX_DISTINCT_OPTIONS };
};

// El cliente Axios global agrega X-XSRF-TOKEN, cabecera que varios
// servidores ArcGIS rechazan en CORS — mismo motivo/comentario que
// LayersSymbologies.vue::fetchArcgis, de donde se porta este patrón. Un host
// inalcanzable no debe tirar la promesa: null (nunca throw) para que el
// caller degrade a soporte no disponible.
const fetchArcgisJson = async (url, signal) => {
  try {
    const response = await fetch(url, { method: "GET", credentials: "omit", signal: signal || AbortSignal.timeout(15000) });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    return null;
  }
};

// Mismo patrón que advancedFilters.js::compileCondition para el operador
// "contains" en dialecto arcgis (UPPER(...) LIKE UPPER('%...%')) — escapa
// comillas simples para no romper el WHERE.
const escapeSqlString = (value) => String(value).replace(/'/g, "''");

// arcgis: ArcGIS 10.1+ soporta returnDistinctValues nativo en el mismo
// /query que ya usa el resto de la app — sin plugins, sin proceso aparte.
// Porta LayersSymbologies.vue::fetchDistinctValues (única implementación
// previa en este repo).
const fetchArcgis = async (layer, field, signal, query) => {
  const where = query ? `UPPER(${field}) LIKE UPPER('%${escapeSqlString(query)}%')` : "1=1";
  const url = `${UrlHandler.getArcgisUrl(layer)}/query?where=${encodeURIComponent(where)}&outFields=${encodeURIComponent(field)}&returnDistinctValues=true&returnGeometry=false&f=json`;
  const data = await fetchArcgisJson(url, signal);
  if (!data || data.error || !data.features) return emptyResult();
  const rawValues = data.features.map((feature) => feature.attributes[field]);
  const count = data.features.length;
  const exceeded = data.exceededTransferLimit === true || count >= MAX_DISTINCT_OPTIONS;
  return { supported: true, values: dedupeSorted(rawValues), exceeded };
};

const escapeXml = (value) => String(value)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// geoserver: WPS gs:PagedUnique — devuelve valores únicos, paginados, de un
// atributo de un WFS GetFeature de referencia. XML Execute 1.0.0, salida
// application/json {featureTypeName, fieldName, size, values}. query arma un
// ogc:PropertyIsLike case-insensitive sobre el GetFeature referenciado (no
// hay input de filtro propio en el proceso).
const buildPagedUniqueXml = (typeName, field, { startIndex, maxFeatures, query } = {}) => {
  const safeField = escapeXml(field);
  const filter = query
    ? `<ogc:Filter><ogc:PropertyIsLike wildCard="*" singleChar="?" escapeChar="!" matchCase="false"><ogc:PropertyName>${safeField}</ogc:PropertyName><ogc:Literal>*${escapeXml(query)}*</ogc:Literal></ogc:PropertyIsLike></ogc:Filter>`
    : "";
  const inputs = [
    `<wps:Input><ows:Identifier>features</ows:Identifier><wps:Reference mimeType="text/xml" xlink:href="http://geoserver/wfs" method="POST"><wps:Body><wfs:GetFeature service="WFS" version="1.0.0" outputFormat="GML2"><wfs:Query typeName="${escapeXml(typeName)}">${filter}</wfs:Query></wfs:GetFeature></wps:Body></wps:Reference></wps:Input>`,
    `<wps:Input><ows:Identifier>fieldName</ows:Identifier><wps:Data><wps:LiteralData>${safeField}</wps:LiteralData></wps:Data></wps:Input>`,
  ];
  if (startIndex != null) inputs.push(`<wps:Input><ows:Identifier>startIndex</ows:Identifier><wps:Data><wps:LiteralData>${startIndex}</wps:LiteralData></wps:Data></wps:Input>`);
  if (maxFeatures != null) inputs.push(`<wps:Input><ows:Identifier>maxFeatures</ows:Identifier><wps:Data><wps:LiteralData>${maxFeatures}</wps:LiteralData></wps:Data></wps:Input>`);
  return `<?xml version="1.0" encoding="UTF-8"?><wps:Execute version="1.0.0" service="WPS" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns="http://www.opengis.net/wps/1.0.0" xmlns:wfs="http://www.opengis.net/wfs" xmlns:wps="http://www.opengis.net/wps/1.0.0" xmlns:ows="http://www.opengis.net/ows/1.1" xmlns:gml="http://www.opengis.net/gml" xmlns:ogc="http://www.opengis.net/ogc" xmlns:xlink="http://www.w3.org/1999/xlink" xsi:schemaLocation="http://www.opengis.net/wps/1.0.0 http://schemas.opengis.net/wps/1.0.0/wpsAll.xsd"><ows:Identifier>gs:PagedUnique</ows:Identifier><wps:DataInputs>${inputs.join("")}</wps:DataInputs><wps:ResponseForm><wps:RawDataOutput mimeType="application/json"><ows:Identifier>result</ows:Identifier></wps:RawDataOutput></wps:ResponseForm></wps:Execute>`;
};

const fetchGeoserver = async (metadata, field, signal, query) => {
  const body = buildPagedUniqueXml(metadata.typeName, field, { maxFeatures: MAX_DISTINCT_OPTIONS, query });
  let response;
  try {
    response = await fetch(`${metadata.baseUrl}/ows?service=WPS`, {
      method: "POST",
      headers: { "Content-Type": "text/xml" },
      body,
      signal,
    });
  } catch (error) {
    return emptyResult();
  }
  if (!response.ok) return emptyResult();
  let data;
  try {
    data = await response.json();
  } catch (error) {
    return emptyResult();
  }
  if (!data || !Array.isArray(data.values)) return emptyResult();
  const exceeded = typeof data.size === "number" && data.size > data.values.length;
  return { supported: true, values: dedupeSorted(data.values), exceeded };
};

export const distinctValuesService = {
  supports(metadata) {
    return !!metadata && ["upload", "arcgis", "geoserver"].includes(metadata.provider);
  },

  async fetch(layer, field, { signal, query } = {}) {
    if (!layer || !field) return emptyResult();
    let metadata;
    try {
      metadata = await attributesTableService.getLayerMetadata(layer);
    } catch (error) {
      return emptyResult();
    }
    // Booleano: siempre exactamente dos opciones, sin tocar ningún
    // proveedor. displayValue() en AttributeFilter.vue ya sabe mostrar
    // "Sí"/"No" para estos valores.
    if (metadata.schema && metadata.schema[field] === "boolean") {
      return { supported: true, values: ["true", "false"], exceeded: false };
    }

    // query (búsqueda server-side) nunca se cachea junto al resultado base:
    // solo se usa cuando la lista completa se truncó (exceeded), un caso
    // acotado que no vale la pena cachear por texto de búsqueda.
    if (!query) {
      const cacheKey = `${layer.id}::${field}`;
      if (cache.has(cacheKey)) return cache.get(cacheKey);
      const result = await this.dispatch(metadata, layer, field, signal, query);
      cache.set(cacheKey, result);
      return result;
    }
    return this.dispatch(metadata, layer, field, signal, query);
  },

  async dispatch(metadata, layer, field, signal, query) {
    if (metadata.provider === "upload") return fetchUpload(layer, field);
    if (metadata.provider === "arcgis") return fetchArcgis(layer, field, signal, query);
    if (metadata.provider === "geoserver") return fetchGeoserver(metadata, field, signal, query);
    return emptyResult();
  },
};

// makeDistinctValuesFetcher — closure atada a UNA capa concreta, para pasar
// hacia abajo por el árbol de componentes en vez de un `layer`/`metadata`
// crudo. AdvancedFilterPanel.vue necesita dos bindings distintos (capa
// fuente vs. capa objetivo del filtro cruzado) para dos subárboles
// hermanos — algo que un solo layer/metadata compartido no puede expresar.
export function makeDistinctValuesFetcher(layer) {
  if (!layer) return null;
  return (field, opts) => distinctValuesService.fetch(layer, field, opts);
}

export function clearDistinctValuesCache() {
  cache.clear();
}
